
import { useState, useEffect, useRef } from "react"
import { useDispatch } from "react-redux"
import useTranslation from "../../hooks/useTranslation"
import { useSelector } from "react-redux"
import type { RootState } from "../../store"
import { fetchMenuData } from "./menuSlice"
import type { AppDispatch } from "../../store"
import { useUrlParams } from "../../utils/urlParams"
import Navbar from "../../components/Navbar"
import Bottom from "../../components/Bottom"
import ProductCard from "../../components/ProductCard"
import BasketButton from "../../components/ui/BasketButton"
import BannerSlider from "../../components/ui/BannerSlider"
import OrderInfoBar from "../../components/ui/OrderInfoBar"
import QRScannerDisplay from "../../components/QRScannerDisplay"
import { useTheme } from "../../contexts/ThemeContext"
// Cart functionality is now handled by Redux slices directly
import { useReduxLanguage } from "../../hooks/useReduxLanguage"
import { useReduxModal } from "../../hooks/useReduxModal"

import { getLocalizedTextFromApiResponse } from './menuSlice'


export default function HomePage() {
  const { primaryColor } = useTheme()
  const { t, currentLanguage: language } = useTranslation()
  const { language: reduxLanguage } = useReduxLanguage()
  const { openModal, closeModal } = useReduxModal()
  const { getOrderInfo: getOrderInfoFromHook, getApiParams: getApiParamsFromHook, hasRequiredParams } = useUrlParams()

  const dispatch = useDispatch<AppDispatch>()

  // Extract API parameters to avoid function dependencies
  const { branchId, typeId } = getApiParamsFromHook()

  // Use menu slice directly
  const { categories, isLoading: loading, error } = useSelector((state: RootState) => state.menu)
  const { token, isAuthenticated } = useSelector((state: RootState) => state.auth)

  // Note: Removed auto-login - users should login when adding to cart
  const hasData = categories.length > 0

  // Check if parameters are valid (exist and are numbers)
  const areParamsValid = (): boolean => {
    if (!hasRequiredParams()) return false
    if (!branchId || !typeId) return false
    if (isNaN(branchId) || isNaN(typeId)) return false
    return true
  }

  // Determine if we should show QR Scanner Display
  // Show QR Scanner when:
  // 1. Required params are missing
  // 2. Params exist but are invalid (not numbers)
  // 3. There's an error and no data loaded
  const shouldShowQRScanner = (): boolean => {
    if (!hasRequiredParams()) return true
    if (!areParamsValid()) return true
    if (error && categories.length === 0) return true
    return false
  }


  // States for data
  const [branchInfo, setBranchInfo] = useState<any>(null)
  const [orderInfo, setOrderInfo] = useState<{
    branchName: string
    orderType: string
    tableNumber: string | null
  }>({ branchName: 'Branch', orderType: 'Branch', tableNumber: null })

  // Active category state
  const [activeCategory, setActiveCategory] = useState<string | number>("")

  // Track if we've initiated fetch for current parameters
  const fetchInitiatedRef = useRef<string>("")

  // Fetch data from API - refetch when parameters or language change
  useEffect(() => {
    const paramKey = `${branchId}-${typeId}-${language}`

    // Skip if we've already initiated fetch for these parameters AND language
    if (fetchInitiatedRef.current === paramKey) {
      return
    }

    // Mark that we've initiated fetch for these parameters
    fetchInitiatedRef.current = paramKey

    // Fetch data when language changes OR when we don't have data
    const shouldFetch = categories.length === 0 || !loading
    
    if (shouldFetch) {
      dispatch(fetchMenuData({ 
        branchId, 
        typeId, 
        language: language,
        forceRefresh: true // Force refresh when language changes
      }))
    }
  }, [branchId, typeId, language, categories.length, loading, dispatch]) // Added language dependency

  // Update branch info when language changes
  useEffect(() => {
    setBranchInfo({
      name: t('restaurant.name'),
    })
  }, [language, t])

  // Update order info from URL parameters
  useEffect(() => {
    const urlOrderInfo = getOrderInfoFromHook()
    setOrderInfo(urlOrderInfo)
  }, [branchId, typeId, getOrderInfoFromHook]) // Update when URL parameters change

  // Update active category when categories change
  useEffect(() => {
    if (!activeCategory && categories.length > 0) {
      const firstCategory = categories[0]
      const categoryId = firstCategory.id || getLocalizedTextFromApiResponse(firstCategory, 'name', language, 'ar')
      setActiveCategory(categoryId)
    }
  }, [categories]) // Remove activeCategory from dependency array to prevent infinite loop

  // Get filtered products based on active category with multilingual support
  const getFilteredProducts = () => {
    if (!categories || categories.length === 0) return []

    // Find active category using localized name extraction
    const activeItems = categories.find((cat) => {
      const catId = cat.id || ""
      const catName = getLocalizedTextFromApiResponse(cat, 'name', language, 'ar')
      
      return catId === activeCategory || catName === activeCategory
    })

    // If we found the category, return its items
    return activeItems ? activeItems.products || [] : []
  }

  const filteredProducts = getFilteredProducts()

  // Transform API data for ProductCard with multilingual support
  const transformApiDataForProductCard = (item: any) => {
    // Extract localized names and descriptions
    const nameAr = getLocalizedTextFromApiResponse(item, 'name', 'ar', 'ar')
    const nameEn = getLocalizedTextFromApiResponse(item, 'name', 'en', 'ar')
    const descriptionAr = getLocalizedTextFromApiResponse(item, 'description', 'ar', 'ar')
    const descriptionEn = getLocalizedTextFromApiResponse(item, 'description', 'en', 'ar')
    
    return {
      id: item.id,
      name: getLocalizedTextFromApiResponse(item, 'name', language, 'ar'),
      description: getLocalizedTextFromApiResponse(item, 'description', language, 'ar'),
      image: item.image,
      price: item.price,
      type: item.type || "product",
      categoryName: activeCategory.toString(),
      itemable: {
        name: {
          ar: nameAr || item.name || '',
          en: nameEn || item.name || '',
        },
        description: {
          ar: descriptionAr || item.description || '',
          en: descriptionEn || item.description || '',
        },
        // Store original API data for modal
        originalData: item,
      },
    }
  }

  // Show QR Scanner Display if parameters are missing, invalid, or there's an error
  if (shouldShowQRScanner()) {
    return <QRScannerDisplay />
  }

  return (
    <div className="w-full " style={{ backgroundColor: '#f5f5f5' }}>
<Navbar />

      {/* Order Information Bar - positioned immediately under navbar */}
      <div className="w-full bg-gray-50 pb-3 px-0">
        <OrderInfoBar
          branchName={orderInfo.branchName}
          orderType={orderInfo.orderType}
          tableNumber={orderInfo.tableNumber}
        />
      </div>

      {/* Main Content */}
      <div className="w-full bg-gray-50">
          <div className="mx-auto pb-8 pt-1 sm:px-5 lg:px-7 px-3">
            {/* Banner Slider */}
            <div className="my-1">
              <BannerSlider
                autoSlideInterval={4}
                className="w-full"
                showDots={false}
                showArrows={false}
                pauseOnHover={false}
              />
            </div>

            {/* Pass category list to Bottom component */}
            <Bottom activeCategory={activeCategory} setActiveCategory={setActiveCategory} categories={categories} />

            {/* Products section */}
            <div className="py-10">
              {categories.length === 0 ? (
                <div className="text-center py-10">
                  <div className="text-gray-400 text-6xl mb-4">🍽️</div>
                  <p className="text-xl text-gray-500">{t("menu.noProducts")}</p>
                </div>
              ) : (
                <>
                  <h2
                    className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-8 text-gray-800 ${language === "ar" ? "text-right" : "text-left"
                      }`}
                  >
                    {(() => {
                      const foundCat = categories.find((cat) => {
                        const catId = cat.id || ""
                        let catName = ""
                        if (typeof cat.name === "object" && cat.name !== null) {
                          const nameObj = cat.name as { en?: string; ar?: string }
                          catName = language === "en" ? (nameObj.en || "") : (nameObj.ar || "")
                        } else {
                          catName = cat.name || ""
                        }
                        return catId === activeCategory || catName === activeCategory
                      })

                      if (foundCat) {
                        if (typeof foundCat.name === "object" && foundCat.name !== null) {
                          const nameObj = foundCat.name as { en?: string; ar?: string }
                          return language === "en" ? (nameObj.en || "") : (nameObj.ar || "")
                        } else {
                          return foundCat.name || ""
                        }
                      }
                      return activeCategory
                    })()}
                  </h2>

                  {/* Responsive grid for cards - Mobile: 2, Tablet: 3, Desktop: 4+, Large: 5+ */}
                  <div
                    className={`grid gap-4 sm:gap-6 lg:gap-8 ${language === "ar" ? "rtl" : "ltr"} 
                      grid-cols-2 
                      md:grid-cols-3 
                      lg:grid-cols-4 
                      xl:grid-cols-5 
                      2xl:grid-cols-6`}
                  >
                    {/* Display filtered products by category */}
                    {filteredProducts.length > 0 ? (
                      filteredProducts.map((item: any) => {
                        // Transform API data to appropriate format for display in ProductCard
                        const productData = transformApiDataForProductCard(item)
                        return productData ? <ProductCard key={productData.id} {...productData} /> : null
                      })
                    ) : (
                      <div className="col-span-full text-center py-10">
                        <div className="text-gray-400 text-6xl mb-4">🍽️</div>
                        <p className="text-xl text-gray-500">{t("menu.noProducts")}</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

      <BasketButton />
    </div>
  )
}
